import { useCallback, useMemo } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Card, CardContent, Chip, Grid, Stack, Typography, useTheme, Button } from '@mui/material'
import { DataGrid, GridColDef, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData, PropertyResult } from '../../../hooks/climateData'
import {
  EmissionTier,
  MetricState,
  stateColorMapping,
  CanonicalPeriod,
  canonicalPeriodLabels,
  metricStateLabels,
  PERIODS,
} from '../common'
import { FloodScoreBadge } from '../common/flood/utils'
import { ago } from 'balkerne-core/time'
import { useHistory } from 'react-router-dom'
import { useCurrentUser } from '../../../hooks/users'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { LoadingValue } from './PortfolioLoading'

function compactNumber(num: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(num)
}

function NumberCell({ value, prefix = '', loading }: { value?: number | null; prefix?: string; loading?: boolean }) {
  return (
    <LoadingValue loading={loading} width={90} fallback="—">
      {typeof value === 'number' ? `${prefix}${compactNumber(value)}` : null}
    </LoadingValue>
  )
}

function StatusChip({ value }: { value?: string | null }) {
  if (!value) return <span>—</span>
  const label = metricStateLabels[value as MetricState] || value
  const color = stateColorMapping[value]
  return (
    <span style={{ background: color, color: 'white', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
      {label}
    </span>
  )
}

function PropertyFilterToolbar() {
  const apiRef = useGridApiContext()
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', px: 0, py: 1 }}>
      <Typography variant="subtitle1">Property Breakdown</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() => apiRef.current?.showFilterPanel?.()}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  )
}

type KPI = {
  avgScore: number | null
  highCount: number
  totalAAL: number | null
  coverageScore: number
  coverageAAL: number
}

type Row = {
  id: number
  name: string
  type: string
  statusScore?: MetricState
  statusAAL?: MetricState
  statusScoreUpdated?: string
  statusAALUpdated?: string
  score: Partial<Record<CanonicalPeriod, number | null>>
  aal: Partial<Record<CanonicalPeriod, number | null>>
}

export default function PortfolioFlood({ emissionTier }: { emissionTier: EmissionTier }) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations, isLoading: locationsLoading } = useLocations({ group_id: groupId })
  const { data: dataScores, isLoading: scoreLoading } = usePortfolioClimateData('fld:score', {
    groupId: groupId ?? undefined,
  })
  const { data: dataAAL, isLoading: aalLoading } = usePortfolioClimateData('fld:average_damage_loss', {
    groupId: groupId ?? undefined,
  })
  const { currencySymbol } = useCurrentUser()

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'fld', emissionTier)),
    [emissionTier, history],
  )

  // Build rows indexed by property id
  const rows: Row[] = useMemo(() => {
    const base: Record<number, Row> = {}
    locations.forEach((property: any) => {
      base[property.id] = {
        id: property.id,
        name: property.name,
        type: property.type?.label ?? 'Unknown',
        score: {},
        aal: {},
        statusScore: undefined,
        statusAAL: undefined,
        statusScoreUpdated: undefined,
        statusAALUpdated: undefined,
      }
    })
    const tierKey = emissionTier

    const ensureRow = (propertyId: number): Row => {
      const existing = base[propertyId]
      if (existing) return existing
      const row: Row = {
        id: propertyId,
        name: `Property ${propertyId}`,
        type: 'Unknown',
        score: {},
        aal: {},
        statusScore: undefined,
        statusAAL: undefined,
        statusScoreUpdated: undefined,
        statusAALUpdated: undefined,
      }
      base[propertyId] = row
      return row
    }

    function pickScore(result: PropertyResult, periodName: CanonicalPeriod): number | null {
      if (!result || !result.data || result.state?.value !== MetricState.SUCCEEDED) return null
      const series = result.data?.data?.[tierKey]?.[periodName]
      const y = Array.isArray(series) && series[0] ? series[0].y : null
      return typeof y === 'number' ? y : null
    }

    function sumAAL(result: PropertyResult, periodName: CanonicalPeriod): number | null {
      if (!result || !result.data || result.state?.value !== MetricState.SUCCEEDED) return null
      const node = result.data?.data?.[tierKey]?.[periodName]
      if (!node || typeof node !== 'object') return null
      let total = 0
      let found = false
      Object.values(node).forEach((targetNode: any) => {
        if (!targetNode || typeof targetNode !== 'object') return
        Object.values(targetNode).forEach((sourceArr: any) => {
          if (Array.isArray(sourceArr) && sourceArr[0] && typeof sourceArr[0].y === 'number') {
            total += sourceArr[0].y
            found = true
          }
        })
      })
      return found ? total : null
    }

    if (dataScores) {
      Object.values(dataScores).forEach(propertyResult => {
        if (!propertyResult) return
        const row = ensureRow(propertyResult.property_id)
        row.statusScore = propertyResult.state?.value
        row.statusScoreUpdated = propertyResult.state?.updated_at || undefined
        PERIODS.forEach(periodName => (row.score[periodName] = pickScore(propertyResult, periodName)))
        base[propertyResult.property_id] = row
      })
    }

    if (dataAAL) {
      Object.values(dataAAL as Record<string, PropertyResult>).forEach(entry => {
        if (!entry) return
        const row = ensureRow(entry.property_id)
        row.statusAAL = entry.state?.value
        row.statusAALUpdated = entry.state?.updated_at || undefined
        PERIODS.forEach(periodName => (row.aal[periodName] = sumAAL(entry, periodName)))
        base[entry.property_id] = row
      })
    }
    return Object.values(base)
  }, [locations, dataScores, dataAAL, emissionTier])

  const KPIs = useMemo(() => {
    const out: Record<CanonicalPeriod, KPI> = {
      [CanonicalPeriod.SHORT_TERM]: { avgScore: null, highCount: 0, totalAAL: null, coverageScore: 0, coverageAAL: 0 },
      [CanonicalPeriod.MID_TERM]: { avgScore: null, highCount: 0, totalAAL: null, coverageScore: 0, coverageAAL: 0 },
      [CanonicalPeriod.LONG_TERM]: { avgScore: null, highCount: 0, totalAAL: null, coverageScore: 0, coverageAAL: 0 },
    }
    PERIODS.forEach(periodName => {
      const scores = rows.map(r => r.score[periodName]).filter((v): v is number => typeof v === 'number')
      const aals = rows.map(r => r.aal[periodName]).filter((v): v is number => typeof v === 'number')
      const mean = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null
      const total = aals.length ? aals.reduce((a, b) => a + b, 0) : null
      const high = rows.filter(r => {
        const v = r.score[periodName]
        return typeof v === 'number' && v >= 4
      }).length
      out[periodName] = {
        avgScore: mean,
        highCount: high,
        totalAAL: total,
        coverageScore: scores.length,
        coverageAAL: aals.length,
      }
    })
    return out
  }, [rows])

  const gridLoading = locationsLoading && locations.length === 0

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Property', width: 240 },
      { field: 'type', headerName: 'Property Type', width: 140 },
      ...PERIODS.map(
        (periodName): GridColDef<Row> => ({
          field: `score_${periodName}`,
          headerName: `${canonicalPeriodLabels[periodName]} Ratings`,
          width: 160,
          valueGetter: params => params.row.score[periodName],
          renderCell: params => (
            <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
              <FloodScoreBadge score={(params.value as number | null) ?? null} size="dense" />
            </LoadingValue>
          ),
        }),
      ),
      ...PERIODS.map(
        (periodName): GridColDef<Row> => ({
          field: `aal_${periodName}`,
          headerName: `${canonicalPeriodLabels[periodName]} AAL`,
          width: 160,
          valueGetter: params => params.row.aal[periodName],
          renderCell: params => (
            <NumberCell value={(params.value as number | null) ?? null} prefix={currencySymbol} loading={aalLoading} />
          ),
        }),
      ),
      {
        field: 'statusScore',
        headerName: 'Rating Status',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusScore ?? null,
        renderCell: p => (
          <LoadingValue loading={scoreLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value} />
          </LoadingValue>
        ),
      },
      {
        field: 'statusScoreUpdated',
        headerName: 'Rating Last Updated',
        width: 160,
        sortable: false,
        valueGetter: p => p.row.statusScoreUpdated ?? undefined,
        type: 'dateTime',
        renderCell: p => (
          <LoadingValue loading={scoreLoading} width={90} fallback="">
            {p.value ? ago(p.value ?? '') : ''}
          </LoadingValue>
        ),
      },
      {
        field: 'statusAAL',
        headerName: 'AAL Status',
        width: 140,
        sortable: false,
        valueGetter: p => p.row.statusAAL ?? null,
        renderCell: p => (
          <LoadingValue loading={aalLoading} variant="rounded" width={80} height={18}>
            <StatusChip value={p.value} />
          </LoadingValue>
        ),
      },
      {
        field: 'statusAALUpdated',
        headerName: 'AAL Last Updated',
        width: 170,
        sortable: false,
        valueGetter: p => p.row.statusAALUpdated ?? undefined,
        type: 'dateTime',
        renderCell: p => (
          <LoadingValue loading={aalLoading} width={90} fallback="">
            {p.value ? ago(p.value ?? '') : ''}
          </LoadingValue>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button size="small" onClick={() => goToProperty(params.row.id)}>
            View property
          </Button>
        ),
      },
    ],
    [currencySymbol, goToProperty, scoreLoading, aalLoading],
  )

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Average Score per period */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Average Risk Rating</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(periodName => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      key={periodName}>
                      <Typography variant="body2">{canonicalPeriodLabels[periodName]}</Typography>
                      <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                        <FloodScoreBadge
                          score={KPIs[periodName].avgScore != null ? KPIs[periodName].avgScore : null}
                          size="dense"
                        />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* High/Very High counts */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline"># of High / Very High Risk Ratings</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(periodName => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      key={periodName}>
                      <Typography variant="body2">{canonicalPeriodLabels[periodName]}</Typography>
                      <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                        <Chip label={KPIs[periodName].highCount} />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Total AAL per period */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline">Total Annual Average Loss</Typography>
                <Stack direction="column" spacing={1} mt={1}>
                  {PERIODS.map(periodName => (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      key={periodName}>
                      <Typography variant="body2">{canonicalPeriodLabels[periodName]}</Typography>
                      <LoadingValue loading={aalLoading} variant="rounded" width={110} height={24}>
                        <Chip
                          label={
                            KPIs[periodName].totalAAL != null
                              ? `${currencySymbol}${compactNumber(KPIs[periodName].totalAAL!)}`
                              : '—'
                          }
                        />
                      </LoadingValue>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tables */}
        <Card>
          <CardContent>
            <div style={{ height: 520, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={r => r.id}
                loading={gridLoading}
                initialState={{
                  sorting: { sortModel: [{ field: `score_${CanonicalPeriod.LONG_TERM}`, sort: 'desc' }] },
                }}
                density="compact"
                onRowDoubleClick={params => goToProperty(params.row.id)}
                disableSelectionOnClick
                components={{ Toolbar: PropertyFilterToolbar }}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                }}
              />
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  )
}
